import { supabase } from '@/integrations/supabase/client';
import { AnalysisAPI, type AnalysisRecord } from '@/lib/api/analysis';
import type { 
  SwingRunSummary, 
  LandmarkBlob, 
  LandmarkFrame,
  CreateRunParams, 
  CompleteRunParams, 
  FailRunParams 
} from '../types';

export class SwingLabRunsService {
  async createRun(params: CreateRunParams): Promise<string> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('swing_analysis_runs')
      .insert({
        user_id: user.id,
        video_asset_id: params.videoAssetId,
        mode: params.mode,
        clip_ms: params.clipMs,
        width: params.width,
        height: params.height,
        policy: params.policy || 'signed',
        detect_cadence: params.detectCadence || 2,
        status: 'started'
      })
      .select('id')
      .single();

    if (error) throw new Error(`Failed to create run: ${error.message}`);
    return data.id;
  }

  async completeRun(params: CompleteRunParams): Promise<void> {
    const { error } = await supabase
      .from('swing_analysis_runs')
      .update({
        status: 'completed',
        processed_frames: params.processedFrames,
        drawn_frames: params.drawnFrames,
        first_result_at_ms: params.firstResultAtMs,
        metrics: params.metrics,
        storage_path: params.storagePath,
        completed_at: new Date().toISOString()
      })
      .eq('id', params.runId);

    if (error) throw new Error(`Failed to complete run: ${error.message}`);
  }

  async failRun(params: FailRunParams): Promise<void> {
    const { error } = await supabase
      .from('swing_analysis_runs')
      .update({
        status: 'failed',
        error: params.error,
        completed_at: new Date().toISOString()
      })
      .eq('id', params.runId);

    if (error) throw new Error(`Failed to mark run as failed: ${error.message}`);
  }

  async getRun(runId: string): Promise<SwingRunSummary | null> {
    // Use AnalysisAPI to read from unified view
    const data = await AnalysisAPI.getAnalysisById(runId);
    if (!data) return null;
    return this.mapAnalysisRecordToSummary(data);
  }

  async listRunsForAsset(videoAssetId: string, limit = 5): Promise<SwingRunSummary[]> {
    // Use AnalysisAPI to read from unified view
    const data = await AnalysisAPI.listAnalysisForAsset(videoAssetId, limit);
    return data.map(this.mapAnalysisRecordToSummary);
  }

  async uploadLandmarkBlob(
    runId: string, 
    kind: 'pose' | 'hands', 
    frames: LandmarkFrame[]
  ): Promise<string> {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) throw new Error('User not authenticated');

    // Convert to NDJSON and compress
    const ndjson = frames.map(frame => JSON.stringify(frame)).join('\n');
    const compressed = new TextEncoder().encode(ndjson);
    
    const fileName = `${userId}/runs/${runId}/${kind}.ndjson.gz`;
    
    const { error: uploadError } = await supabase.storage
      .from('swing-lab')
      .upload(fileName, compressed, {
        contentType: 'application/gzip',
        upsert: true
      });

    if (uploadError) throw new Error(`Failed to upload blob: ${uploadError.message}`);

    // Create blob record
    const { error: insertError } = await supabase
      .from('swing_landmark_blobs')
      .insert({
        run_id: runId,
        kind,
        storage_path: fileName,
        frame_count: frames.length
      });

    if (insertError) throw new Error(`Failed to create blob record: ${insertError.message}`);

    return fileName;
  }

  async downloadLandmarkBlob(runId: string, kind: 'pose' | 'hands'): Promise<LandmarkFrame[]> {
    // Get blob record
    const { data: blob, error: blobError } = await supabase
      .from('swing_landmark_blobs')
      .select('storage_path')
      .eq('run_id', runId)
      .eq('kind', kind)
      .single();

    if (blobError) throw new Error(`Failed to find blob: ${blobError.message}`);

    // Download from storage
    const { data, error } = await supabase.storage
      .from('swing-lab')
      .download(blob.storage_path);

    if (error) throw new Error(`Failed to download blob: ${error.message}`);

    // Decompress and parse NDJSON
    const text = await data.text();
    return text.split('\n')
      .filter(line => line.trim())
      .map(line => JSON.parse(line));
  }

  private mapToSummary(data: any): SwingRunSummary {
    return {
      id: data.id,
      userId: data.user_id,
      videoAssetId: data.video_asset_id,
      policy: data.policy,
      clipMs: data.clip_ms,
      width: data.width,
      height: data.height,
      mode: data.mode,
      status: data.status,
      processedFrames: data.processed_frames,
      drawnFrames: data.drawn_frames,
      detectCadence: data.detect_cadence,
      firstResultAtMs: data.first_result_at_ms,
      metrics: data.metrics,
      storagePath: data.storage_path,
      error: data.error,
      createdAt: data.created_at,
      completedAt: data.completed_at,
      // AI analysis fields - use legacy column for direct DB reads
      aiMediapipeV1: data.ai_mediapipe_v1,
      aiMeta: data.ai_meta
    };
  }

  private mapAnalysisRecordToSummary(data: AnalysisRecord): SwingRunSummary {
    return {
      id: data.id,
      userId: data.user_id,
      videoAssetId: data.video_asset_id,
      policy: data.policy,
      clipMs: data.clip_ms,
      width: data.width,
      height: data.height,
      mode: data.mode,
      status: data.status,
      processedFrames: data.processed_frames,
      drawnFrames: data.drawn_frames,
      detectCadence: data.detect_cadence,
      firstResultAtMs: data.first_result_at_ms,
      metrics: data.metrics,
      storagePath: data.storage_path,
      error: data.error,
      createdAt: data.created_at,
      completedAt: data.completed_at,
      // AI analysis fields - use unified meta from view
      aiMediapipeV1: data.ai_mediapipe_v1,
      aiMeta: data.meta // Use unified meta field from view
    };
  }
}

export const swingLabRuns = new SwingLabRunsService();

/**
 * Helper function to resolve videoId (video_asset_id) to analysisId (runId)
 * Returns the latest completed analysis run for the given video asset ID
 */
export async function getAnalysisIdForVideo(videoAssetId: string): Promise<string | null> {
  const { data } = await supabase
    .from('swing_analysis_runs')
    .select('id')
    .eq('video_asset_id', videoAssetId)
    .eq('status', 'completed')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  return data?.id || null;
}